import React from 'react';
//import GoogleMapsWrapper from 'Common/Map/GoogleMaps';
import OpenLayersMapWrapper from 'Common/Map/OpenLayers';
import LoginDialogContainer from 'Components/Map/Containers/LoginDialogContainer';
import Button from '@material-ui/core/Button';
import { parseLinksFromDetectorMapping, parseLanesFromDetectorMapping } from 'Util/DetectorMappingParser';
import TssClient from 'Clients/TssClient';
import CctvClient from 'Clients/CctvClient';
import DmsClient from 'Clients/DmsClient';
import SaaClient from 'Clients/SaaClient';
import {
    Link,
    Node, Lane, LinkData, LanePollData, Dms,
    Camera,
    IBaseResponse,
    DetectorConfig,
    DetectorMap,
    RetrieveDataResponseOfStringAndDetectorConfig,
    RetrieveDataResponseOfStringAndNode,
    RetrieveDataResponseOfStringAndLinkGeometry,
    RetrieveDataResponseOfStringAndDetectorMap,
    RetrieveDataResponseOfStringAndIdAndStatusOfLinkPollStatus,
    RetrieveDataResponseOfStringAndCamera,
    RetrieveDataResponseOfStringAndDms,
    LinkGeometry,
    RetrievePermissionsResponse,
    UserData
} from 'NSwag/ServerClient/Client.nswag';
import { ILinkMaps } from 'Store/index';
import AppSideMenu from 'Common/Menu/AppSideMenu';
import { IHashMap } from "DataLibrary/Common/IHashMap";
import { mapAdjacentLinks } from 'Util/LinkGeometryParser';

interface IState {
    open: boolean;
    mapType: 'googleMaps' | 'openLayers' | undefined;
    menuOpen: boolean;
}

interface IProps {
    jwt: string | null;
    dmses: IHashMap<Dms>;
    tssLinks: IHashMap<Link>;
    tssDetectors: IHashMap<DetectorConfig>;
    tssDetectorMaps: IHashMap<DetectorMap>;
    cameras: IHashMap<Camera>;
    setJwt(jwt: string): void;
    addDmses(dmses: Dms[]): void;
    addDetectorConfigs(detectors: DetectorConfig[]): void;
    addNodes(nodes: Node[]): void;
    addLinkGeometries(geometries: LinkGeometry[]): void;
    addLinkMaps(maps: ILinkMaps): void,
    addLanes(lanes: Lane[]): void;
    addLinks(links: Link[]): void;
    addDetectorMaps(detectorMaps: DetectorMap[]): void;
    addLinkPollData(linkData: LinkData[]): void;
    addLanePollData(laneData: LanePollData[]): void;
    addCameras(cameras: Camera[]): void;
    addUserData(userData: UserData): void;
}

/*
 * Render the Map and its children
 */
class Map extends React.Component<IProps, IState> {
    
    optionsMenuAnchor: any;

    /**
     * @constructor
     * @param {any} props
     */
    constructor(props: IProps) {
        super(props);

        this.state = {
            open: false,
            menuOpen: false,
            mapType: 'openLayers'
        }
    }

    /**
     * Handles when the component is initially rendered in the DOM.
     */
    componentDidMount = () => {
        if (this.props.jwt != null) {
            this.retrieveCache();
        }
    }

    /**
     * Handles when the component updates from new props or state.
     * @param prevProps - properties prior to the update
     */
    componentDidUpdate = (prevProps: IProps) => {

        // If the jwt was cleared then stop web sockets
        if (prevProps.jwt !== null && this.props.jwt === null) {
            this.setState({menuOpen: false});
        }

        // If the jwt was set then start web sockets and retrieve cache
        if (prevProps.jwt == null && prevProps.jwt !== this.props.jwt) {
            this.retrieveCache();
        }
    }

    /**
     * Retrieves the initial cache from the 
     */
    retrieveCache = () => {
        let cacheOperations: { endpoint: Promise<IBaseResponse | null>, action(response: IBaseResponse): void }[] = [
            {
                endpoint: TssClient.getDetectorConfig(null),
                action: (response: RetrieveDataResponseOfStringAndDetectorConfig) => {
                    if (response.data) {
                        this.props.addDetectorConfigs(Object.values(response.data!));
                    }
                }
            },
            {
                endpoint: TssClient.getNode(null),
                action: (response: RetrieveDataResponseOfStringAndNode) => {
                    if (response.data) {
                        this.props.addNodes(Object.values(response.data!));
                    }
                }
            },
            {
                endpoint: TssClient.getLinkGeometry(null),
                action: (response: RetrieveDataResponseOfStringAndLinkGeometry) => {
                    if (response.data) {
                        this.props.addLinkGeometries(Object.values(response.data!));
                        this.props.addLinkMaps(mapAdjacentLinks(response.data!))
                    }
                }
            },
            {
                endpoint: CctvClient.getCamera(null),
                action: (response: RetrieveDataResponseOfStringAndCamera) => {
                    if (response.data) {
                        this.props.addCameras(Object.values(response.data!));
                    }
                }
            },
            {
                endpoint: DmsClient.getDms(null),
                action: (response: RetrieveDataResponseOfStringAndDms) => {
                    if (response.data) {
                        this.props.addDmses(Object.values(response.data!));
                    }
                }
            },
            {
                endpoint: TssClient.getDetectorMap(null),
                action: (response: RetrieveDataResponseOfStringAndDetectorMap) => {
                    if (response.data) {
                        let links: Link[] = [];
                        let lanes: Lane[] = [];
                        Object.keys(response.data).forEach((detectorKey: string) => {
                            if (response.data && response.data[detectorKey] != null) {
                                let detectorMapping: DetectorMap = response.data[detectorKey];
                                lanes = [...lanes, ...parseLanesFromDetectorMapping(detectorMapping)];
                                links = [...links, ...parseLinksFromDetectorMapping(detectorMapping)];
                            }
                        });
                        
                        this.props.addLanes(lanes);
                        this.props.addLinks(links);
                        this.props.addDetectorMaps(Object.values(response.data!));
                    }
                }
            },
            {
                endpoint: TssClient.getlinkPollStatus(null),
                action: (response: RetrieveDataResponseOfStringAndIdAndStatusOfLinkPollStatus) => {
                    if (response.data) {
                        let linkData = Object.values(response.data!);
                        let lanePollData = linkData.reduce<LanePollData[]>((laneData, linkData) => ([
                            ...laneData,
                            ...linkData.status!.linkData!.lanePollData!
                        ]), []);
                        
                        let truLinkData = linkData.map((el) => el.status!.linkData!);

                        this.props.addLanePollData(lanePollData);
                        this.props.addLinkPollData(truLinkData);
                    }
                }
            },
            {
                endpoint: SaaClient.getPermissionData(),
                action: (response: RetrievePermissionsResponse) => {
                    if (response.data) {
                        if (response.data.userData) {
                            this.props.addUserData(response.data.userData);
                        }
                    }
                }
            }
        ];

        cacheOperations.forEach(operation => {
            operation.endpoint
                .then((response: IBaseResponse | null) => {
                    if (response) {
                        if (response.hasError) {
                            console.log(response.error);
                        } else {
                            operation.action(response);
                        }
                    }
                })
                .catch(error => {
                    console.log(error);
                });
        });
    }

    handleMenuToggle = () => {
        this.setState((previousState: IState) => ({ open: !previousState.open }));
    };

    handleDrawerToggle = () => {
        this.setState((previousState: IState) => ({menuOpen: !previousState.menuOpen}));
    }

    handleMenuClose = (event: React.ChangeEvent<{}>) => {
        if (this.optionsMenuAnchor.contains(event.target)) {
            return;
        }

        this.setState({ open: false });
    };

    

    setMapType = (mapType: any) => () => {
        this.setState({ mapType, open: false });
    }

    

    /**
    * Renders the component when called by react
    * @return { GoogleMap } - The Google map being used to render links and other objects
    */
    render() {
        const { tssLinks, tssDetectors, tssDetectorMaps, cameras, jwt, dmses } = this.props;
        const { mapType, open, menuOpen } = this.state;

        return (
            <div>
                <AppSideMenu
                    open={menuOpen}
                    anchor='left'
                    onClose={this.handleDrawerToggle}
                />
                <LoginDialogContainer>
                    <div>
                        {
                            jwt && 
                            <Button onClick={this.handleDrawerToggle}
                                variant="outlined"
                            >
                                Subsystems
                            </Button>
                        }
                    </div>
                </LoginDialogContainer>
                <OpenLayersMapWrapper
                    tssLinks={tssLinks}
                    tssDetectors={tssDetectors}
                    tssDetectorMaps={tssDetectorMaps}
                    cameras={cameras}
                    dmses={dmses}
                />
            </div>
        );
    }
}

/**
 * exports the default Map to be used for imports
 */
export default Map